import Vue from 'vue'
import { REASON_TEXT, generateImportId } from '@/lib/customer-import-csv'

export const STATUS = {
  IDLE: 'IDLE',
  AWAITING_PREFLIGHT: 'AWAITING_PREFLIGHT',
  PREFLIGHT: 'PREFLIGHT',
  IMPORTING: 'IMPORTING',
  SUMMARY: 'SUMMARY',
  DONE: 'DONE',
  FAILED: 'FAILED',
  TIMED_OUT: 'TIMED_OUT'
}

const PAGE_STATE = {
  [STATUS.IDLE]: 'upload',
  [STATUS.AWAITING_PREFLIGHT]: 'upload',
  [STATUS.FAILED]: 'upload',
  [STATUS.PREFLIGHT]: 'preflight',
  [STATUS.IMPORTING]: 'preflight',
  [STATUS.TIMED_OUT]: 'preflight',
  [STATUS.SUMMARY]: 'summary',
  [STATUS.DONE]: 'done'
}

export const PREFLIGHT_TIMEOUT_MS = 180000
// The importer Lambda timeout (900s) plus a minute of delivery slack.
export const IMPORT_TIMEOUT_MS = 960000

const SAMPLE_SIZE = 5
const JOINED_FIELDS = ['name', 'phone', 'email']

// Module scope rather than store state: the parsed rows are guest PII, so they stay out of devtools
// snapshots and reactive watchers. Only the five preview rows, which are rendered anyway, reach state.
let localRows = []
let watchdogId = null

// A timed out import may still be running, so its late result is still rendered.
const AWAITING_RESULT = [STATUS.IMPORTING, STATUS.TIMED_OUT]

const clearWatchdog = () => {
  if (watchdogId) clearTimeout(watchdogId)
  watchdogId = null
}

const initialState = () => ({
  status: STATUS.IDLE,
  importId: null,
  preflight: null,
  samples: [],
  mappedFields: {},
  result: null,
  failure: null,
  rowCountMismatch: false,
  localRowCount: 0
})

const failedTitle = (count) =>
  count ? `Importing ${count} customers failed! Try again` : 'Importing customers failed! Try again'

export default {
  namespaced: true,

  state: initialState(),

  getters: {
    selectStatus: (state) => state.status,
    selectPageState: (state) => PAGE_STATE[state.status] || 'upload',
    selectFailedRows: (state) => {
      const errors = (state.result && state.result.errors) || []
      const columnFor = Object.keys(state.mappedFields).reduce((acc, column) => {
        acc[state.mappedFields[column]] = column
        return acc
      }, {})

      return errors.map(({ row, reason }) => {
        const local = state.rowCountMismatch ? null : localRows[row - 1]
        const joined = JOINED_FIELDS.reduce((acc, field) => {
          acc[field] = (local && columnFor[field] && local[columnFor[field]]) || ''
          return acc
        }, {})
        return { row, reason: REASON_TEXT[reason] || REASON_TEXT.UNKNOWN, ...joined }
      })
    }
  },

  mutations: {
    SET_STATUS(state, status) {
      state.status = status
    },
    START(state, { importId, localRowCount }) {
      Object.assign(state, initialState(), {
        status: STATUS.AWAITING_PREFLIGHT,
        importId,
        localRowCount
      })
    },
    SET_PREFLIGHT(state, { preflight, samples, rowCountMismatch }) {
      state.preflight = preflight
      state.samples = samples
      state.rowCountMismatch = rowCountMismatch
      state.status = STATUS.PREFLIGHT
    },
    SET_MAPPED_FIELDS(state, mappedFields) {
      state.mappedFields = mappedFields
    },
    SET_RESULT(state, result) {
      state.result = result
    },
    SET_FAILURE(state, failure) {
      state.failure = failure
      state.status = STATUS.FAILED
    },
    RESET(state) {
      Object.assign(state, initialState())
    }
  },

  actions: {
    async startPreflight({ commit, dispatch, state }, { rows }) {
      clearWatchdog()
      localRows = rows
      const importId = generateImportId()
      commit('START', { importId, localRowCount: rows.length })

      watchdogId = setTimeout(() => {
        watchdogId = null
        if (state.importId !== importId || state.status !== STATUS.AWAITING_PREFLIGHT) return
        commit('SET_FAILURE', { stage: 'preflight', reason: 'TIMEOUT' })
      }, PREFLIGHT_TIMEOUT_MS)

      try {
        await dispatch('customer/importCustomers', { preflight: true, importId }, { root: true })
      } catch (error) {
        if (state.importId === importId && state.status === STATUS.AWAITING_PREFLIGHT) {
          clearWatchdog()
          commit('SET_FAILURE', { stage: 'preflight', reason: 'REQUEST' })
        }
        throw error
      }
    },

    async startImport({ commit, dispatch, state }, { mappedFields }) {
      if (state.status !== STATUS.PREFLIGHT) return

      clearWatchdog()
      const { importId } = state
      commit('SET_MAPPED_FIELDS', { ...mappedFields })
      commit('SET_STATUS', STATUS.IMPORTING)

      watchdogId = setTimeout(() => {
        watchdogId = null
        if (state.importId !== importId || state.status !== STATUS.IMPORTING) return
        commit('SET_STATUS', STATUS.TIMED_OUT)
        Vue.notify({
          type: 'warn',
          title: 'Import still processing',
          text: 'Check the Customers list in a few minutes.'
        })
      }, IMPORT_TIMEOUT_MS)

      try {
        await dispatch(
          'customer/importCustomers',
          { mappedFields: state.mappedFields, preflight: false, importId },
          { root: true }
        )
      } catch (error) {
        if (state.importId === importId && state.status === STATUS.IMPORTING) {
          clearWatchdog()
          commit('SET_FAILURE', { stage: 'import', reason: 'REQUEST' })
        }
        throw error
      }
    },

    receiveSampled({ commit, state }, payload) {
      if (!payload || payload.importId !== state.importId) return
      if (state.status !== STATUS.AWAITING_PREFLIGHT) return

      clearWatchdog()
      commit('SET_PREFLIGHT', {
        preflight: { count: payload.count, columns: payload.columns || [] },
        samples: localRows.slice(0, SAMPLE_SIZE),
        rowCountMismatch: payload.count !== localRows.length
      })
    },

    receiveImported({ commit, state }, payload) {
      if (!payload || payload.importId !== state.importId) return
      if (!AWAITING_RESULT.includes(state.status)) return

      clearWatchdog()
      commit('SET_RESULT', {
        count: payload.count,
        importedCount: payload.importedCount,
        errorCount: payload.errorCount || 0,
        errors: payload.errors || [],
        truncated: payload.truncated === true
      })
      Vue.notify({
        title: 'Import completed',
        duration: 30000,
        ignoreDuplicates: true,
        text: `${payload.importedCount} customers imported.`
      })
      if (payload.errorCount > 0) {
        commit('SET_STATUS', STATUS.SUMMARY)
        return
      }
      localRows = []
      commit('SET_STATUS', STATUS.DONE)
    },

    receiveFailed({ commit, state }, payload) {
      if (!payload || payload.importId !== state.importId) return
      const importing = AWAITING_RESULT.includes(state.status)
      if (!importing && state.status !== STATUS.AWAITING_PREFLIGHT) return

      clearWatchdog()
      const stage = payload.stage || (importing ? 'import' : 'preflight')
      commit('SET_FAILURE', { stage, reason: 'SERVER' })
      Vue.notify({ type: 'error', title: failedTitle(payload.count || localRows.length) })
    },

    // TODO(OVA-5166): remove once nexus prod publishes to the private Pusher channel. Reads only
    // counts and column key names from the legacy broadcast; rows always come from the local file.
    receiveLegacyMessage({ dispatch, state, rootGetters }, message) {
      const { type, detail } = message || {}
      const onlineUser = rootGetters['user/onlineUser']
      if (!detail || !onlineUser || detail.userId !== onlineUser._id) return

      const importId = state.importId
      const errors = Array.isArray(detail.errors) ? detail.errors : []
      // The legacy broadcast has no importId, so a late event from an earlier import is told
      // apart by its row count.
      const sameFile = detail.count === localRows.length

      if (type === 'customers-import-sampled' && sameFile) {
        const firstSample = (detail.samples || [])[0] || {}
        dispatch('receiveSampled', {
          importId,
          count: detail.count,
          columns: Object.keys(firstSample)
        })
      } else if (type === 'customers-imported' && sameFile) {
        dispatch('receiveImported', {
          importId,
          count: detail.count,
          importedCount: detail.count - errors.length,
          errorCount: errors.length,
          errors: [],
          truncated: errors.length > 0
        })
      } else if (type === 'customers-import-failed') {
        dispatch('receiveFailed', { importId })
      }
    },

    finishSummary({ commit, state }) {
      if (state.status !== STATUS.SUMMARY) return
      localRows = []
      commit('SET_STATUS', STATUS.DONE)
    },

    reset({ commit }) {
      clearWatchdog()
      localRows = []
      commit('RESET')
    }
  }
}
